import React from "react";
import { Navigate, NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { firstNameOf } from "../lib/profile";
import { colors } from "../components/auth/authStyles";
import OrdersTab from "../components/account/OrdersTab";
import ProfileTab from "../components/account/ProfileTab";

const TABS = [
  { to: "/account", label: "Order history" },
  { to: "/account/profile", label: "Profile" },
];

// "2026-10-03T..." -> "October 2026"
const memberSince = (createdAt) =>
  createdAt
    ? new Date(createdAt).toLocaleDateString("en-CA", { month: "long", year: "numeric" })
    : null;

const Account = ({ tab = "orders" }) => {
  const { user, profile, loading } = useAuth();

  if (loading) return <div style={{ minHeight: "100vh" }} />;
  if (!user) {
    const from = tab === "profile" ? "/account/profile" : "/account";
    return <Navigate to="/login" replace state={{ from }} />;
  }

  const since = memberSince(user.created_at);

  return (
    <div style={{ paddingTop: "72px", minHeight: "100vh" }}>
      <style>
        {`
          .account-hero {
            position: relative;
            overflow: hidden;
            background-color: ${colors.black};
          }
          .account-hero-inner,
          .account-body {
            max-width: 1200px;
            margin: 0 auto;
            padding-left: 1.5rem;
            padding-right: 1.5rem;
          }
          .account-hero-inner {
            position: relative;
            z-index: 1;
            padding-top: 4rem;
          }
          .account-ball {
            position: absolute;
            right: 5%;
            bottom: -150px;
            width: 300px;
            height: 300px;
            border-radius: 999px;
            background-image: radial-gradient(circle at 35% 30%, #5fb4d6 0%, #1f5f7a 45%, #0b2430 85%);
          }
          .account-badge {
            display: inline-flex;
            align-items: center;
            gap: 0.45rem;
            margin-top: 1.25rem;
            padding: 0.4rem 0.85rem;
            border-radius: 999px;
            border: 1px solid rgba(34, 211, 238, 0.45);
            background-color: rgba(34, 211, 238, 0.1);
            color: ${colors.cyan};
            font-size: 0.85rem;
            font-weight: 600;
          }
          .account-tabs {
            display: flex;
            gap: 2.25rem;
            margin-top: 2.5rem;
          }
          .account-tab {
            padding-bottom: 1rem;
            border-bottom: 3px solid transparent;
            color: #9a9a9a;
            font-size: 1rem;
            font-weight: 600;
            text-decoration: none;
          }
          .account-tab:hover {
            color: white;
          }
          .account-tab.active {
            color: white;
            border-bottom-color: ${colors.cyan};
          }
          .account-body {
            padding-top: 4rem;
            padding-bottom: 6rem;
          }
          .account-section-head {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: baseline;
            gap: 0.5rem 1.5rem;
            margin-bottom: 1.5rem;
          }
          .account-section-title {
            font-size: 2rem;
            font-weight: 900;
            letter-spacing: -0.02em;
            margin: 0;
          }
          @media (max-width: 768px) {
            .account-hero-inner {
              padding-top: 3rem;
            }
            .account-ball {
              width: 180px;
              height: 180px;
              bottom: -110px;
              right: -40px;
            }
            .account-body {
              padding-top: 2.5rem;
            }
          }
        `}
      </style>

      <section className="account-hero">
        <div className="account-ball" aria-hidden="true" />
        <div className="account-hero-inner">
          <span
            style={{
              fontSize: "0.8rem",
              fontWeight: 600,
              letterSpacing: "0.14em",
              color: colors.cyan,
            }}
          >
            YOUR 900 ACCOUNT
          </span>
          <h1
            style={{
              fontSize: "clamp(2.25rem, 6vw, 3.5rem)",
              lineHeight: 1.05,
              fontWeight: 900,
              letterSpacing: "-0.025em",
              color: "white",
              margin: "0.75rem 0 0",
            }}
          >
            Hi, {firstNameOf(profile?.full_name, user.email)}.
          </h1>
          <p
            style={{
              color: "#c7c7c7",
              fontSize: "1.1rem",
              lineHeight: 1.5,
              maxWidth: "520px",
              margin: "0.75rem 0 0",
            }}
          >
            Your orders, receipts and contact details, all in one place.
          </p>
          {since && (
            <span className="account-badge">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" />
              </svg>
              Member since {since}
            </span>
          )}
          <nav className="account-tabs" aria-label="Account sections">
            {TABS.map(({ to, label }) => (
              <NavLink key={to} to={to} end className="account-tab">
                {label}
              </NavLink>
            ))}
          </nav>
        </div>
      </section>

      <div className="account-body">
        {tab === "profile" ? <ProfileTab /> : <OrdersTab />}
      </div>
    </div>
  );
};

export default Account;
